import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState, useRef, useCallback, useEffect } from "react";
import assert from "assert";
import { useVirtualizer } from "@tanstack/react-virtual";
import "../ThemeProvider/ThemeProvider.mjs";
import "../ThemePortal/ThemePortal.mjs";
import "../Text/TextContext.mjs";
import "../ThemeProvider/ThemeContext.mjs";
import "../ThemeProvider/VanillaThemeContainer.mjs";
import "../private/DefaultMessagesContext.mjs";
import "../ThemeProvider/BreakpointContext.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import { PublicBox } from "../Box/Box.mjs";
import "../Box/BackgroundContext.mjs";
import "../Divider/Divider.css.mjs";
import "../Heading/HeadingContext.mjs";
import "../Hidden/Hidden.css.mjs";
import "../../css/negativeMargin/negativeMargin.css.mjs";
import "../Accordion/AccordionContext.mjs";
import "../private/defaultTextProps.mjs";
import "../../css/typography.css.mjs";
import "../private/MaxLines/MaxLines.css.mjs";
import { Columns } from "../Columns/Columns.mjs";
import { Column } from "../Column/Column.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../private/touchable/virtualTouchable.css.mjs";
import "../private/hideFocusRings/hideFocusRings.css.mjs";
import "../Accordion/AccordionItem.css.mjs";
import "../Actions/ActionsContext.mjs";
import "../../css/textAlignedToIcon.css.mjs";
import "../Alert/Alert.css.mjs";
import "../Autosuggest/Autosuggest.mjs";
import "../Avatar/Avatar.css.mjs";
import "../Badge/Badge.mjs";
import "../Breadcrumb/Breadcrumb.css.mjs";
import "../Box/ColoredBox.mjs";
import { Button } from "../Button/Button.mjs";
import "../ButtonIcon/ButtonIcon.mjs";
import "../ButtonLink/ButtonLink.mjs";
import "../private/Keyline/Keyline.css.mjs";
import { Checkbox } from "../Checkbox/Checkbox.mjs";
import "../Checkbox/CheckboxStandalone.mjs";
import "../ContentBlock/ContentBlock.css.mjs";
import "../DateField/DateField.mjs";
import "../private/Field/FloatingLabel.css.mjs";
import "../private/Field/Field.css.mjs";
import "../DateField/DateField.css.mjs";
import "../private/Modal/Modal.mjs";
import "../private/Modal/ModalContent.mjs";
import "../private/Modal/ModalContext.mjs";
import "../TextLink/TextLink.mjs";
import "../TextLinkButton/TextLinkButton.css.mjs";
import "../private/AvoidWidowIcon/AvoidWidowIcon.css.mjs";
import "../Dropdown/Dropdown.mjs";
import "../HiddenVisually/HiddenVisually.css.mjs";
import "../Link/Link.mjs";
import "../List/List.css.mjs";
import "../Loader/Loader.css.mjs";
import { Skeleton } from "../Skeleton/Skeleton.mjs";
import "../MenuRenderer/MenuRenderer.mjs";
import "../MenuItem/MenuItem.mjs";
import "../MenuItemCheckbox/MenuItemCheckbox.mjs";
import "../MenuItemDivider/MenuItemDivider.mjs";
import "../MenuItem/MenuItemLink.mjs";
import "../OverflowMenu/OverflowMenu.css.mjs";
import "../MonthPicker/MonthPicker.mjs";
import "../Pagination/Pagination.css.mjs";
import "../PasswordField/PasswordField.mjs";
import "../Pincode/Pincode.mjs";
import "../PopoverRenderer/PopoverRenderer.mjs";
import "../Pressable/Pressable.mjs";
import "../RadioGroup/RadioGroup.mjs";
import "../RadioGroup/RadioItem.mjs";
import "../RadioGroup/RadioItemBlock.mjs";
import { emptyContainer, table, columnHeader, listContainer, cellContainerRecipe } from "./SelectableList.css.mjs";
import { ListItem } from "./ListItem.mjs";
import { BulkActions } from "../private/BulkActions/BulkActions.mjs";
import "../Spread/Spread.css.mjs";
import "../Stepper/Step.mjs";
import "../Stepper/Stepper.mjs";
import "../Tabs/Tab.mjs";
import "../Table/Table.mjs";
import "../Table/Table.css.mjs";
import "../Tabs/TabsProvider.mjs";
import "../Tabs/TabListContext.mjs";
import "../Tabs/Tabs.css.mjs";
import "../Tabs/TabPanelsContext.mjs";
import "../Tag/Tag.css.mjs";
import "../Textarea/Textarea.mjs";
import "../TextDropdown/TextDropdown.css.mjs";
import "../TextField/TextField.mjs";
import "../Tiles/Tiles.css.mjs";
import "../Toggle/Toggle.mjs";
import "../useToast/ToastContext.mjs";
import "../TooltipRenderer/TooltipRenderer.mjs";
import "../TimeField/DateSegment.css.mjs";
import "../private/FieldButtonIcon/FieldButtonIcon.mjs";
import "../MultiStepModal/MultiStepModal.mjs";
import "../SearchField/SearchField.mjs";
import "../RadioBar/RadioBarContext.mjs";
import "../RadioBar/RadioBar.css.mjs";
import "../../themes/vars.css.mjs";
import "../RadioBar/RadioBar.mjs";
const SelectableList = ({
  items,
  heading,
  width = "full",
  itemsSelectedText,
  clearAllText,
  submitButton,
  bulkActions,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  emptyComponent
}) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const containerRef = useRef(null);
  const selectedRowSize = selectedItems.length;
  const hasSelectedRows = selectedRowSize > 0;
  const isEmpty = items.length === 0;
  const selectedData = Array.from(selectedItems);
  assert(!bulkActions || bulkActions.length <= 2, "bulkActions can contain 2 extra actions maximum");
  assert(bulkActions || submitButton, "must contain bulkActions or submitButton");
  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? items.length + 1 : items.length,
    getScrollElement: () => containerRef.current,
    estimateSize: useCallback(() => 76, []),
    overscan: 5
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  useEffect(() => {
    if (selectedData.some((item) => !items.find((i) => i.id === item))) {
      setSelectedItems((existing) => existing.filter((item) => items.find((i) => i.id === item)));
    }
  }, [items, selectedData]);
  useEffect(() => {
    const [lastItem] = virtualItems.reverse();
    if (!lastItem) {
      return;
    }
    if (lastItem.index >= items.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage == null ? void 0 : fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, items.length, isFetchingNextPage, virtualItems]);
  return /* @__PURE__ */ jsxs(
    PublicBox,
    {
      gap: "small",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flexStart",
      alignItems: "flexEnd",
      width: "full",
      children: [
        isEmpty ? /* @__PURE__ */ jsx(
          PublicBox,
          {
            className: emptyContainer,
            border: "borderNeutralLight",
            borderRadius: "large",
            paddingY: "xlarge",
            paddingX: "small",
            width: "full",
            children: emptyComponent
          }
        ) : /* @__PURE__ */ jsxs(PublicBox, { width: "full", children: [
          /* @__PURE__ */ jsx(
            BulkActions,
            {
              visible: hasSelectedRows,
              selectedItems: selectedData,
              selectedItemsLength: selectedRowSize,
              clearAllSelectedRows: () => {
                setSelectedItems([]);
              },
              clearAllText,
              totalItems: items.length,
              itemsSelectedText,
              bulkActions
            }
          ),
          /* @__PURE__ */ jsxs(
            PublicBox,
            {
              alignItems: "stretch",
              className: table,
              border: "borderNeutralLight",
              borderRadius: hasSelectedRows ? "none" : "small",
              style: {
                width: width === "full" ? "100%" : width
              },
              role: "table",
              children: [
                /* @__PURE__ */ jsx(
                  PublicBox,
                  {
                    className: columnHeader,
                    background: "tableBodyAlternate",
                    paddingX: "xsmall",
                    paddingY: "xsmall",
                    role: "columnheader",
                    children: /* @__PURE__ */ jsx(Columns, { space: "none", alignY: "center", children: /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(PublicBox, { children: /* @__PURE__ */ jsx(
                      Checkbox,
                      {
                        id: "select-all",
                        label: heading,
                        onChange: () => {
                          setSelectedItems((oldList) => {
                            if (oldList.length === items.length) {
                              return [];
                            }
                            return [...items.map((item) => item.id)];
                          });
                        },
                        checked: items.map((item) => selectedItems.includes(item.id))
                      }
                    ) }) }) })
                  }
                ),
                /* @__PURE__ */ jsx(PublicBox, { ref: containerRef, className: listContainer, role: "list", children: /* @__PURE__ */ jsx(
                  PublicBox,
                  {
                    style: {
                      height: `${rowVirtualizer.getTotalSize()}px`
                    },
                    width: "full",
                    position: "relative",
                    children: /* @__PURE__ */ jsx(Fragment, { children: virtualItems.map((virtualRow) => {
                      const isLoaderRow = virtualRow.index > items.length - 1;
                      const row = items[virtualRow.index];
                      return /* @__PURE__ */ jsx(
                        PublicBox,
                        {
                          className: cellContainerRecipe({ even: virtualRow.index % 2 === 0 }),
                          width: "full",
                          style: {
                            transform: `translateY(${virtualRow.start}px)`
                          },
                          "data-index": virtualRow.index,
                          ref: rowVirtualizer.measureElement,
                          children: isLoaderRow ? /* @__PURE__ */ jsxs(PublicBox, { display: "flex", alignItems: "center", gap: "xsmall", children: [
                            /* @__PURE__ */ jsx(Skeleton.Shape, { width: 24, height: 24 }),
                            /* @__PURE__ */ jsxs(
                              PublicBox,
                              {
                                display: "flex",
                                alignItems: "flexStart",
                                flexDirection: "column",
                                width: "full",
                                gap: "xsmall",
                                children: [
                                  /* @__PURE__ */ jsx(Skeleton.Shape, { width: "100%", height: 24 }),
                                  /* @__PURE__ */ jsx(Skeleton.Shape, { width: "60%", height: 24 })
                                ]
                              }
                            )
                          ] }) : /* @__PURE__ */ jsx(
                            ListItem,
                            {
                              id: row.id,
                              label: row.label,
                              description: row.description,
                              badges: row.badges,
                              icon: row.icon,
                              selected: selectedItems.includes(row.id),
                              disabled: row.disabled,
                              onSelect: () => {
                                setSelectedItems((oldList) => {
                                  if (oldList.includes(row.id)) {
                                    return oldList.filter((id) => id !== row.id);
                                  }
                                  return [...oldList, row.id];
                                });
                              }
                            }
                          )
                        },
                        virtualRow.index
                      );
                    }) })
                  }
                ) })
              ]
            }
          )
        ] }),
        submitButton ? /* @__PURE__ */ jsx(PublicBox, { children: /* @__PURE__ */ jsx(
          Button,
          {
            ...submitButton,
            disabled: !hasSelectedRows,
            onClick: () => {
              submitButton == null ? void 0 : submitButton.onClick(selectedData);
            },
            children: submitButton == null ? void 0 : submitButton.label
          }
        ) }) : null
      ]
    }
  );
};
export {
  SelectableList
};
